import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import { atom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { ScrollView } from "react-native";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { CodeInput } from "@whoosh/design-system/src/components/controls/CodeInput";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { VStack, Text } from "@whoosh/design-system/src/components/primitive";

import { VerificationModalStepStateAtom } from "../../../../atoms/stores";
import { useCreateEmailVerification } from "../../../../hooks/useCreateEmailVerification";
import { useCreatePhoneVerification } from "../../../../hooks/useCreatePhoneVerification";
import { useVerifyEmailCode } from "../../../../hooks/useVerifyEmailCode";
import { useVerifyPhoneCode } from "../../../../hooks/useVerifyPhoneCode";
import { VerifyCodeStep_session$key } from "./__generated__/VerifyCodeStep_session.graphql";

const contactInfoRawValueAtom = atom((get) => {
  const state = get(VerificationModalStepStateAtom);
  if (state.step === "VerifyCode") {
    return state.email || state.phone;
  }
  return null;
});

const isPhoneOrEmailAtom = atom((get) => {
  const state = get(VerificationModalStepStateAtom);
  if (state.step === "VerifyCode") {
    if (state.email) {
      return "email";
    } else if (state.phone) {
      return "phone";
    }
  }
  return null;
});

const phoneUtil = PhoneNumberUtil.getInstance();
const RESEND_COOLDOWN_SECONDS = 5;

const contactInfoDisplayAtom = atom((get) => {
  const isPhoneOrEmail = get(isPhoneOrEmailAtom);
  const value = get(contactInfoRawValueAtom);
  if (isPhoneOrEmail === "phone" && value) {
    //If it's phone we must format the national number
    return phoneUtil.format(
      phoneUtil.parseAndKeepRawInput(value, "US"),
      PhoneNumberFormat.NATIONAL
    );
  } else {
    return value ?? "";
  }
});

const verificationTypeAtom = atom((get) => {
  const state = get(VerificationModalStepStateAtom);
  if (state.step === "VerifyCode") {
    return state.verificationType;
  }
  return null;
});

export const VerifyCodeStep = ({
  sessionRef,
  clubSlug,
}: {
  sessionRef: VerifyCodeStep_session$key | null;
  clubSlug?: string;
}) => {
  const session = useFragment(VerifyCodeStepFragmentString, sessionRef);
  const clubName = session?.currentClientProfile?.member?.club.name;
  const [didSubmitVerification, setDidSubmitVerification] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const contactInfoRawValue = useAtomValue(contactInfoRawValueAtom);
  const isPhoneOrEmail = useAtomValue(isPhoneOrEmailAtom);
  const { showToast } = useToast();
  const [createEmailVerification, isCreatingEmailVerification] =
    useCreateEmailVerification();
  const [createPhoneVerification, isCreatingPhoneVerification] =
    useCreatePhoneVerification();
  const [verifyEmailCode, isVerifyingEmail] = useVerifyEmailCode();
  const [verifyPhoneCode, isVerifyingPhone] = useVerifyPhoneCode(clubSlug);
  const verificationType = useAtomValue(verificationTypeAtom);
  const contactInfoDisplay = useAtomValue(contactInfoDisplayAtom);

  useEffect(() => {
    if (resendCooldownSeconds === 0) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setResendCooldownSeconds((currentSeconds) => {
        return Math.max(currentSeconds - 1, 0);
      });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [resendCooldownSeconds]);

  const handleSendResendCodeToast = () => {
    setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    showToast({
      description: t`Verification code sent to: ${contactInfoDisplay}`,
      semanticStyle: "success",
      toastId: "resend-verification-code",
    });
  };

  const isResending =
    isCreatingEmailVerification || isCreatingPhoneVerification;
  const isResendDisabled = isResending || resendCooldownSeconds > 0;

  const handleResendVerification = () => {
    if (!contactInfoRawValue || isResendDisabled) {
      return null;
    }
    if (isPhoneOrEmail === "email") {
      createEmailVerification(contactInfoRawValue, handleSendResendCodeToast);
    } else {
      createPhoneVerification(contactInfoRawValue, handleSendResendCodeToast);
    }
  };
  const handleError = () => {
    setCurrentCode(undefined);
    setHasError(true);
    setDidSubmitVerification(false);
  };

  const handleCodeSubmission = (code: string) => {
    if (
      isPhoneOrEmail === "email" &&
      contactInfoRawValue &&
      !isVerifyingEmail &&
      !didSubmitVerification
    ) {
      setDidSubmitVerification(true);
      verifyEmailCode({
        email: contactInfoRawValue,
        code,
        onError: handleError,
      });
    } else if (
      isPhoneOrEmail === "phone" &&
      contactInfoRawValue &&
      !isVerifyingPhone &&
      !didSubmitVerification
    ) {
      setDidSubmitVerification(true);
      verifyPhoneCode({
        phoneNumber: contactInfoRawValue,
        code,
        onError: handleError,
      });
    }
  };

  const [hasError, setHasError] = useState(false);
  const [currentCode, setCurrentCode] = useState<string | undefined>(undefined);
  const displayedClubName = clubName ?? "";
  const contactMethod =
    isPhoneOrEmail === "phone" ? t`phone number` : t`email address`;

  return (
    <VStack paddingHorizontal={6} paddingBottom={6} flexGrow={1} width="100%">
      <ScrollView style={{ flexGrow: 1 }}>
        {verificationType === "CreatingAccountAndVerifyingOtherContact" ? (
          <VStack mb={6}>
            <Text mb={4} variant={"mainTitle2"}>{t`Come here often?`}</Text>
            <Text
              mb={6}
              variant={"mainBaseMedium"}
            >{t`We've found a customer at ${displayedClubName} associated with this ${contactMethod}. We need to verify it to give you access to this profile.`}</Text>
            <VStack gap={2}>
              <Text variant="mainHeading">{t`Enter the code sent to:`}</Text>
              <Text variant="mainBaseSemiBold">{contactInfoDisplay}</Text>
            </VStack>
          </VStack>
        ) : (
          <VStack mb={6}>
            <Text
              marginBottom={3}
              variant={"mainSmallSemiBold"}
              color="text-secondary"
            >
              {t`Verify your ` + " " + isPhoneOrEmail}
            </Text>
            <VStack gap={2}>
              <Text variant={"mainTitle2"}>{t`Enter the code sent to:`}</Text>
              <Text variant={"mainBaseSemiBold"}>{contactInfoDisplay}</Text>
            </VStack>
          </VStack>
        )}

        <CodeInput
          name={"verification input"}
          onChange={(code) => {
            setHasError(false);
            setCurrentCode(code);
          }}
          length={6}
          onSubmit={(code) => {
            handleCodeSubmission(code);
          }}
          value={currentCode}
          hasError={hasError}
        />
      </ScrollView>
      <VStack gap={6}>
        <Button
          variant={"ghost"}
          onPress={handleResendVerification}
          accessibilityLabel={t`Resend button`}
          isDisabled={isResendDisabled}
          testID="verify-code-step-resend-button"
        >
          {t`Resend`}
        </Button>
      </VStack>
    </VStack>
  );
};

export const VerifyCodeStepFragmentString = graphql`
  fragment VerifyCodeStep_session on Session {
    currentClientProfile {
      member {
        club {
          name
        }
      }
    }
  }
`;
